import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Dialog } from './dialog.js';
import { Button } from './button.js';

/**
 * Promise-based confirm, themed — replaces native window.confirm() so the
 * prompt matches the app instead of the browser chrome. (Design-system pass
 * D4/P8.)
 *
 * Usage: const confirm = useConfirm(); … if (await confirm({ title, … })) {…}
 * The async signature is a drop-in for the synchronous `window.confirm(...)`
 * call sites — only `await` is added.
 */
export interface ConfirmOptions {
  title: string;
  /** Body text — supports newlines. */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `destructive` paints the confirm button in the danger tone. */
  tone?: 'default' | 'destructive';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** App-root provider — mount once near the top of the tree. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    setOpts(next);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    setOpen(false);
    resolverRef.current?.(ok);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={open}
        onClose={() => settle(false)}
        {...(opts?.title ? { title: opts.title } : {})}
      >
        {opts?.message && (
          <p className="whitespace-pre-line text-body leading-snug text-[var(--fg-2)]">
            {opts.message}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => settle(false)}>
            {opts?.cancelLabel ?? 'Mégse'}
          </Button>
          <Button
            variant={opts?.tone === 'destructive' ? 'destructive' : 'primary'}
            onClick={() => settle(true)}
            autoFocus
          >
            {opts?.confirmLabel ?? 'Megerősítés'}
          </Button>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/** Returns an async confirm() — resolves true on confirm, false on cancel/Esc. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}
